import React, { useState } from "react";
import { PackageCheck, PackageX, Store, Warehouse, Boxes } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// Presentación única de `disponibilidad` (backend services/disponibilidad_material.py).
// verde = en almacén DNF · azul = en punto de venta · mixto = ambos · ámbar = parcial X/Y · rojo = pendiente.
export const UBICACION = {
  almacen: { label: "En almacén", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40", text: "text-emerald-300", Icon: Warehouse },
  pdv: { label: "En punto de venta", cls: "bg-sky-500/20 text-sky-200 border-sky-500/40", text: "text-sky-300", Icon: Store },
  mixto: { label: "Almacén + punto de venta", cls: "bg-teal-500/20 text-teal-100 border-teal-400/40", text: "text-teal-200", Icon: Boxes },
};
const PARCIAL = { cls: "bg-amber-500/20 text-amber-200 border-amber-500/40", text: "text-amber-300" };
const PENDIENTE = { label: "Pendiente", cls: "bg-rose-500/20 text-rose-200 border-rose-500/40", text: "text-rose-300", Icon: PackageX };

export function describirDisponibilidad(d) {
  if (!d) return { key: "pendiente", label: "Pendiente", detalle: "Sin información de material", ...PENDIENTE };
  if (d.estado === "sin_material") {
    return d.candidata
      ? { key: "sin_material", label: "Sin material", detalle: "Trabajo sin material que producir o recibir", cls: "bg-zinc-700/50 text-zinc-300 border-zinc-600", text: "text-zinc-300", Icon: PackageCheck }
      : { key: "pendiente", label: "Sin componentes", detalle: "Sin componentes: no es enrutable", ...PENDIENTE };
  }
  if (d.estado === "pendiente") return { key: "pendiente", label: "Pendiente", detalle: `0/${d.total} materiales disponibles`, ...PENDIENTE };
  const u = UBICACION[d.ubicacion] || UBICACION.almacen;
  const donde = d.ubicacion === "mixto" ? `${d.en_almacen} en almacén · ${d.en_pdv} en punto de venta` : u.label;
  if (d.estado === "parcial") {
    return { key: "parcial", label: `Parcial ${d.listos}/${d.total}`, detalle: `${d.listos} de ${d.total} materiales disponibles (${donde}) · envío parcial`, ...PARCIAL, Icon: u.Icon };
  }
  return { key: `completo_${d.ubicacion}`, label: d.ubicacion === "mixto" ? "Disponible (mixto)" : u.label, detalle: `Completo · ${d.listos}/${d.total} · ${donde}`, cls: u.cls, text: u.text, Icon: u.Icon };
}

const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");

/** Texto compacto del badge: «1/1 · 20/10 · FR» (recepción real) · «1/2 · varios» · «1/1» (legacy sin datos). */
export function textoCompacto(d, recepcion) {
  if (!d?.total) return "";
  const base = `${d.listos}/${d.total}`;
  if (!recepcion) return base;
  if (recepcion.varios) return `${base} · varios`;
  return `${base}${recepcion.fecha ? ` · ${fmtDM(recepcion.fecha)}` : ""}${recepcion.iniciales ? ` · ${recepcion.iniciales}` : ""}`;
}

function DetalleComponentes({ v, componentes }) {
  return (
    <div className="space-y-1" data-testid="material-detalle">
      <div className="font-semibold">{v.label}</div>
      <div className="text-zinc-400">{v.detalle}</div>
      {componentes?.length > 0 && (
        <ul className="mt-1 space-y-0.5 border-t border-zinc-800 pt-1">
          {componentes.map((c) => {
            const u = c.ubicacion ? UBICACION[c.ubicacion] : null;
            const r = c.recepcion;
            return (
              <li key={c.id} className="flex items-start gap-1.5 text-[11px]" data-testid={`material-detalle-comp-${c.id}`} data-recibido={u ? "1" : "0"}>
                <span className={`mt-0.5 ${u ? u.text : "text-rose-300"}`}>{u ? <u.Icon className="w-3 h-3" /> : <PackageX className="w-3 h-3" />}</span>
                <span className="min-w-0">
                  <span className="text-zinc-100">{c.nombre || "Componente"}</span>
                  <span className="text-zinc-500"> · {u ? u.label.toLowerCase() : (c.estado || "pendiente")}</span>
                  {u && r && <span className="text-zinc-300"> · recibido {fmtDM(r.fecha)}{r.iniciales ? ` · ${r.iniciales}` : ""}</span>}
                  {u && !r && <span className="text-zinc-600"> · sin fecha de recepción</span>}
                  {!u && <span className="text-rose-300 font-semibold"> · PENDIENTE</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function MaterialBadge({ disponibilidad, recepcion, componentes, compact = false, className = "", testId }) {
  const v = describirDisponibilidad(disponibilidad);
  const Icon = v.Icon || PackageCheck;
  const [open, setOpen] = useState(false);
  const conDetalle = Array.isArray(componentes) && componentes.length > 0;
  const texto = compact ? textoCompacto(disponibilidad, recepcion) : v.label;
  const chip = (
    <span data-testid={testId} data-material={v.key} data-recepcion={recepcion ? (recepcion.varios ? "varios" : `${recepcion.fecha}|${recepcion.iniciales}`) : undefined}
      role={conDetalle ? "button" : undefined} tabIndex={conDetalle ? 0 : undefined}
      onClick={conDetalle ? (e) => { e.stopPropagation(); setOpen((o) => !o); } : undefined}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold leading-none whitespace-nowrap ${v.cls} ${conDetalle ? "cursor-pointer" : ""} ${className}`}>
      <Icon className="w-3 h-3" />
      {texto}
    </span>
  );
  const contenido = conDetalle ? <DetalleComponentes v={v} componentes={componentes} /> : (
    <><div className="font-semibold">{v.label}</div><div className="text-zinc-400">{v.detalle}</div></>
  );
  if (!conDetalle) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>{chip}</TooltipTrigger>
          <TooltipContent side="top" className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs max-w-xs">{contenido}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  // Con detalle: hover → tooltip · tap/click → popover (tablet/móvil)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>{chip}</TooltipTrigger>
          </PopoverTrigger>
          {!open && <TooltipContent side="top" className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs max-w-xs">{contenido}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
      <PopoverContent side="top" onClick={(e) => e.stopPropagation()} className="w-72 bg-zinc-900 border-zinc-700 text-zinc-100 text-xs p-2.5" data-testid={testId ? `${testId}-popover` : undefined}>
        {contenido}
      </PopoverContent>
    </Popover>
  );
}

// Chip por componente: `ubicacion` = almacen | pdv | null
export function UbicacionChip({ ubicacion, estado, testId }) {
  const u = ubicacion ? UBICACION[ubicacion] : null;
  const Icon = u ? u.Icon : PackageX;
  return (
    <span data-testid={testId} title={u ? `${u.label} · disponible para montar` : "Pendiente de recepción"}
      className={`text-[10px] font-mono inline-flex items-center gap-1 ${u ? u.text : "text-rose-300"}`}>
      <Icon className="w-3 h-3" />{estado}
    </span>
  );
}

export function LeyendaMaterial({ className = "" }) {
  const items = [
    ["almacen", "En almacén"], ["pdv", "En punto de venta"], ["mixto", "Mixto"],
  ];
  return (
    <div className={`flex flex-wrap items-center gap-2 text-[10px] text-zinc-400 ${className}`} data-testid="leyenda-material">
      <span className="uppercase tracking-wider">Material:</span>
      {items.map(([k, l]) => { const u = UBICACION[k]; return <span key={k} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${u.cls}`}><u.Icon className="w-3 h-3" />{l}</span>; })}
      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${PARCIAL.cls}`}>Parcial X/Y</span>
      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${PENDIENTE.cls}`}><PackageX className="w-3 h-3" />Pendiente</span>
    </div>
  );
}
